import React, { useEffect, useState, useRef } from 'react';
import { Megaphone, Pin, ChevronRight } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { formatWIB } from '@/lib/datetime';

const SEVERITY_STYLES = {
  info: 'bg-sky-50 border-sky-200 text-sky-900',
  success: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  warning: 'bg-amber-50 border-amber-200 text-amber-900',
  critical: 'bg-rose-50 border-rose-200 text-rose-900',
};

function relativeDate(iso) {
  if (!iso) return '';
  try {
    return formatWIB(iso, 'Date', { day: '2-digit', month: 'long', year: 'numeric' }, iso);
  } catch { return iso; }
}

/**
 * Card pengumuman yang ditampilkan di dashboard non-admin (Guru/Siswa/Wali Kelas/dll).
 * Max 3 pengumuman, dengan tombol "Lihat semua".
 */
export default function AnnouncementsCard() {
  const [anns, setAnns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previousAnnIds, setPreviousAnnIds] = useState(new Set());
  const audioRef = useRef(null);
  const hasInteractedRef = useRef(false);
  const nav = useNavigate();

  // Initialize audio element once
  useEffect(() => {
    audioRef.current = new Audio('/sounds/notification-bell.mp3');
    audioRef.current.volume = 0.7;
    audioRef.current.preload = 'auto';

    // Enable audio on first user interaction
    const enableAudio = () => {
      if (!hasInteractedRef.current) {
        hasInteractedRef.current = true;
        console.log('Audio enabled after user interaction');

        // Try to "unlock" audio by playing silent sound (browser autoplay workaround)
        if (audioRef.current) {
          audioRef.current.volume = 0;
          audioRef.current.play()
            .then(() => {
              audioRef.current.pause();
              audioRef.current.currentTime = 0;
              audioRef.current.volume = 0.7;
              console.log('✓ Audio context unlocked');
            })
            .catch(() => {
              console.log('Audio unlock skipped');
            });
        }
      }
    };

    document.addEventListener('click', enableAudio, { once: true });
    document.addEventListener('keydown', enableAudio, { once: true });

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const playNotificationSound = async () => {
    // Only play if user has interacted with the page (browser autoplay policy)
    if (!hasInteractedRef.current) {
      console.log('[Audio] Skipped - waiting for user interaction (browser autoplay policy)');
      return;
    }

    try {
      // Always create fresh Audio element for reliability
      const audio = new Audio('/sounds/notification-bell.mp3');
      audio.volume = 0.7;
      await audio.play();
      console.log('✓ Notification sound played successfully');
    } catch (err) {
      console.error('Could not play notification sound:', err.message);
    }
  };

  const loadAnnouncements = () => {
    api.get('/announcements')
      .then(({ data }) => {
        const newAnns = data || [];
        const currentIds = new Set(newAnns.map(a => a.id));

        // Use callback form to get the actual previous state
        setPreviousAnnIds((prevIds) => {
          console.log('[AnnouncementsCard] Poll update:', {
            previousCount: prevIds.size,
            currentCount: currentIds.size,
            isInitialLoad: prevIds.size === 0
          });

          // Check for new announcements (only after initial load)
          if (prevIds.size > 0) {
            const newAnnouncements = newAnns.filter(ann => !prevIds.has(ann.id));

            if (newAnnouncements.length > 0) {
              console.log(`🔔 [NEW ANNOUNCEMENT] Detected ${newAnnouncements.length} new announcement(s):`,
                newAnnouncements.map(a => ({ id: a.id, title: a.title }))
              );
              playNotificationSound();
            } else {
              console.log('[AnnouncementsCard] No new announcements');
            }
          } else {
            console.log('[AnnouncementsCard] Initial load - sound disabled');
          }

          // Return new state
          return currentIds;
        });

        // Update announcements list
        setAnns(newAnns);
      })
      .catch((err) => {
        console.error('[AnnouncementsCard] Failed to load:', err);
        setAnns([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    // Initial load
    loadAnnouncements();

    // Poll for new announcements every 30 seconds
    const interval = setInterval(loadAnnouncements, 30000);

    return () => clearInterval(interval);
  }, []);

  if (loading) return null;
  if (!anns.length) return null;

  const top = anns.slice(0, 3);

  return (
    <Card data-testid="announcements-card" className="border-[#006837]/15 shadow-sm">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <Megaphone className="h-5 w-5 text-[#006837]" />
            Pengumuman
          </CardTitle>
          <p className="text-xs text-slate-500 mt-0.5">{anns.length} pengumuman aktif</p>
        </div>
        <div className="flex gap-2">
          {/* Test button - only in development */}
          {process.env.NODE_ENV === 'development' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                console.log('🔔 Manual test sound trigger');
                playNotificationSound();
              }}
              className="h-8 text-xs"
              title="Test notification sound"
            >
              🔊 Test
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => nav('/pengumuman')} className="h-8 text-xs" data-testid="btn-all-announcements">
            Lihat semua <ChevronRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2 pt-0">
        {top.map((a) => (
          <div key={a.id} data-testid={`announcement-${a.id}`}
               className={`rounded-lg border px-3.5 py-3 ${SEVERITY_STYLES[a.severity] || SEVERITY_STYLES.info}`}>
            <div className="flex items-start gap-2">
              {a.is_pinned && <Pin className="h-3.5 w-3.5 mt-1 shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-sm">{a.title}</p>
                  <Badge variant="outline" className="text-[10px] py-0 px-1 bg-white/60 capitalize">{a.severity}</Badge>
                  {!a.is_read && <Badge className="text-[9px] py-0 px-1 bg-rose-500 text-white border-0">BARU</Badge>}
                </div>
                <div className="text-xs text-slate-700 mt-1 prose prose-sm max-w-none prose-p:my-0.5 prose-headings:my-1">
                  <ReactMarkdown>{a.body}</ReactMarkdown>
                </div>
                <div className="flex items-center gap-2 mt-1.5 text-[10px] text-slate-500">
                  <span>{relativeDate(a.created_at)}</span>
                  {a.created_by_name && <span>• oleh {a.created_by_name}</span>}
                </div>
              </div>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
